import { useState, useEffect } from "react";
import { adminFetch } from "../../lib/adminApi";
import { toast } from "sonner";

interface MediaAsset {
  key: string;
  type: "image" | "raw";
  url: string;
  thumbnailUrl: string | null;
  size: number;
}

export function useAdminMedia() {
  const [mediaList, setMediaList] = useState<MediaAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [previewAsset, setPreviewAsset] = useState<MediaAsset | null>(null);

  const fetchMedia = async () => {
    setLoading(true);
    try {
      const res = await adminFetch("/api/admin/media");
      if (res?.success && res.media) setMediaList(res.media);
      else toast.error("Failed to load media assets");
    } catch (err: any) {
      toast.error(err.message || "Failed to load media assets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMedia();
  }, []);

  const toggleSelect = (key: string) => {
    setSelectedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const selectAll = () => {
    if (selectedKeys.length === mediaList.length) {
      setSelectedKeys([]);
    } else {
      setSelectedKeys(mediaList.map((m) => m.key));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedKeys.length === 0) return;
    if (!confirm(`Permanently delete the ${selectedKeys.length} selected asset(s)?`)) return;
    setDeleting(true);
    try {
      const res = await adminFetch("/api/admin/media", { keys: selectedKeys }, "DELETE");
      if (res?.success) {
        toast.success(`Successfully deleted ${selectedKeys.length} file(s).`);
        setSelectedKeys([]);
        fetchMedia();
      } else {
        toast.error(res?.error || "Failed to delete files");
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to delete files");
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteSingle = async (key: string) => {
    if (!confirm("Are you sure you want to delete this file?")) return;
    setDeleting(true);
    try {
      const res = await adminFetch("/api/admin/media", { keys: [key] }, "DELETE");
      if (res?.success) {
        toast.success("File deleted successfully.");
        setSelectedKeys((prev) => prev.filter((k) => k !== key));
        fetchMedia();
      } else {
        toast.error(res?.error || "Failed to delete file");
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to delete file");
    } finally {
      setDeleting(false);
    }
  };

  return {
    mediaList,
    loading,
    selectedKeys,
    deleting,
    previewAsset,
    setPreviewAsset,
    fetchMedia,
    toggleSelect,
    selectAll,
    handleDeleteSelected,
    handleDeleteSingle
  };
}
